import { fetchShopPricing } from '../lib/supabase/api/fetch-shop-pricing';
import usePrintStore from '../store/printStore';

// Default values in case shop-specific pricing is not available (updated to match A4 user pricing)
const DEFAULT_BW_BASE_PRICE = 13.50;
const DEFAULT_COLOR_BASE_PRICE = 112.00;

// Cache for shop pricing organized by paper type
let shopPricingCache: {
  [paperType: string]: {
    monochrome_cost: number;
    color_cost: number;
  };
} = {};

// Store the previous shop ID to detect shop changes
let cachedShopId: string | null = null;

/**
 * Set pricing directly from shop object (avoids async DB call)
 * @param shopPricing Pricing object from shop with all paper types
 */
export function setShopPricingFromObject(shopPricing: {
  [paperType: string]: {
    user_monochrome_cost: number;
    user_color_cost: number;
    shop_monochrome_cost: number;
    shop_color_cost: number;
  };
}): void {
  console.log('[setShopPricingFromObject] Setting pricing from shop object:', shopPricing);
  
  // Convert shop pricing format to cache format
  Object.keys(shopPricing).forEach(paperType => {
    shopPricingCache[paperType] = {
      monochrome_cost: shopPricing[paperType].user_monochrome_cost,
      color_cost: shopPricing[paperType].user_color_cost
    };
  });
  
  console.log('[setShopPricingFromObject] Cache updated:', shopPricingCache);
}

/**
 * Loads pricing from DB (fallback only)
 */
export async function loadShopPricing(paperType: string = 'A4'): Promise<void> {
  try {
    const selectedShopId = usePrintStore.getState().selectedShop;
    
    console.log('[loadShopPricing] Loading pricing for:', { selectedShopId, paperType });
    
    if (!selectedShopId) {
      console.warn('No shop selected, using default pricing');
      shopPricingCache[paperType] = {
        monochrome_cost: DEFAULT_BW_BASE_PRICE,
        color_cost: DEFAULT_COLOR_BASE_PRICE
      };
      return;
    }

    // Clear cache if shop changed
    if (cachedShopId !== selectedShopId) {
      console.log('[loadShopPricing] Shop changed, clearing cache');
      shopPricingCache = {};
      cachedShopId = selectedShopId;
    }

    // Fallback: Call DB if pricing not already set
    console.log('[loadShopPricing] Fetching from DB as fallback');
    const pricing = await fetchShopPricing(selectedShopId, paperType);
    
    console.log('[loadShopPricing] Received pricing from DB:', pricing);
    
    if (pricing) {
      shopPricingCache[paperType] = {
        monochrome_cost: pricing.monochrome_cost,
        color_cost: pricing.color_cost
      };
      console.log('[loadShopPricing] Cached pricing from DB:', shopPricingCache[paperType]);
    } else {
      // Fallback to defaults if pricing not available
      console.warn('[loadShopPricing] No pricing data, using defaults');
      shopPricingCache[paperType] = {
        monochrome_cost: DEFAULT_BW_BASE_PRICE,
        color_cost: DEFAULT_COLOR_BASE_PRICE
      };
    }
  } catch (error) {
    console.error('Error loading shop pricing:', error);
    // Use defaults on error
    shopPricingCache[paperType] = {
      monochrome_cost: DEFAULT_BW_BASE_PRICE,
      color_cost: DEFAULT_COLOR_BASE_PRICE
    };
  }
}

export const fetchBWPrice = (paperType: string = 'A4'): number => {
  // If cache doesn't exist for this paper type, trigger reload (async)
  if (!shopPricingCache[paperType]) {
    loadShopPricing(paperType).catch(err => console.error('Failed to load shop pricing:', err));
  }
  return shopPricingCache[paperType]?.monochrome_cost ?? DEFAULT_BW_BASE_PRICE;
};


export const fetchColorPrice = (paperType: string = 'A4'): number => {
  // If cache doesn't exist for this paper type, trigger reload (async)
  if (!shopPricingCache[paperType]) {
    loadShopPricing(paperType).catch(err => console.error('Failed to load shop pricing:', err));
  }
  return shopPricingCache[paperType]?.color_cost ?? DEFAULT_COLOR_BASE_PRICE;
};

// Store the previous shop ID to detect changes
let previousShopId: string | null = usePrintStore.getState().selectedShop;

// Set up a subscription to the store to automatically refresh pricing when the shop changes
const unsubscribe = usePrintStore.subscribe(
  (state) => {
    // Only reload pricing if the shop has actually changed
    const selectedShop = state.selectedShop;
    if (selectedShop !== previousShopId) {
      console.log('Shop changed, refreshing pricing data');
      previousShopId = selectedShop;
      loadShopPricing();
    }
  }
);

// Clean up subscription when the module is unloaded (important for hot reloading)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    unsubscribe();
  });
}

// Initialize shop pricing when the module is loaded
loadShopPricing().catch(err => console.error('Failed to initialize shop pricing:', err));
